import { Tooltip } from '@dc-shared-web/components/base/Tooltip'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@dc-shared-web/components/shadcn/dropdown-menu'
import {
  Breadcrumb,
  BreadcrumbEllipsis,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from '@dc-shared-web/components/shadcn/breadcrumb'
import { useIsOverflowing } from '@dc-shared-web/hooks/utils/useIsOverflowing'
import { isEmpty } from 'lodash'
import React, { FC, Fragment } from 'react'

export interface BaseBreadcrumbProps {
  /** Array of elements to display in the breadcrumb. The "url" property is optional because items without a url are considered the current page (aka the last item). If provided, they'll just be regular breadcrumb links. */
  items: {
    label: string
    url?: string
  }[]
  /** The component to use for the breadcrumb links. We need this prop to be able to use the Link component from Next.js. If not passed, the default is an anchor tag. */
  LinkComponent?: JSX.ElementType
  /** Whether to show a separator after the last item. Useful when you do want to render a custom current breadcrumb item outside of this component. */
  showSeparatorAfterLastItem?: boolean
  /** Whether to show the second to last item. */
  showSecondToLastItem?: boolean
}

export const BaseBreadcrumb: React.FC<BaseBreadcrumbProps> = ({
  items,
  LinkComponent = 'a',
  showSeparatorAfterLastItem,
  showSecondToLastItem,
}) => {
  /*
    Items array is separated into:
    - first: always visible
    - n middle elements:
      - if we have only 1 element, we just show it as a breadcrumb link
      - if we have more than 1 element, we show it as a dropdown menu
    - secondToLast: always visible because we consider the parent of the current element is important
    - last: always visible
  */
  const [first, ...rest] = items
  const middle = rest.slice(0, -2)
  const secondToLast = rest[rest.length - 2]
  const last = rest[rest.length - 1]

  if (!showSecondToLastItem && !isEmpty(secondToLast)) {
    middle.push(secondToLast)
  }

  const hasMoreThanOneItem = items.length > 1

  const hasMiddleItems = middle.length > 0
  const firstMiddleItem = middle[0]
  const hasMoreThanOneMiddleItem = middle.length > 1

  const hasSecondToLastItem = !isEmpty(secondToLast)

  const hasLastItem = !isEmpty(last)

  if (isEmpty(items) || isEmpty(first)) {
    return null
  }

  return (
    <Breadcrumb className='tw-w-full'>
      <BreadcrumbList className='tw-flex-nowrap tw-overflow-hidden'>
        <BreadcrumbItem className='tw-hidden md:tw-flex'>
          <BreadcrumbLink
            dataTestId={first.label.toLowerCase().replace(/\s+/g, '-')}
            asChild
          >
            <LinkComponent href={first.url} className='tw-overflow-hidden'>
              <TruncatedText label={first.label} />
            </LinkComponent>
          </BreadcrumbLink>
        </BreadcrumbItem>
        {hasMoreThanOneItem && (
          <BreadcrumbSeparator className='tw-hidden md:tw-flex' />
        )}
        {hasMiddleItems && (
          <>
            {hasMoreThanOneMiddleItem ? (
              <>
                <DropdownMenu>
                  <DropdownMenuTrigger className='tw-hidden md:tw-flex'>
                    <BreadcrumbEllipsis />
                  </DropdownMenuTrigger>
                  <DropdownMenuContent>
                    {middle.map((item) => (
                      <DropdownMenuItem
                        className='tw-cursor-pointer'
                        key={item.label}
                        asChild
                      >
                        <BreadcrumbLink
                          dataTestId={item.label
                            .toLowerCase()
                            .replace(/\s+/g, '-')}
                          asChild
                        >
                          <LinkComponent
                            href={item.url}
                            className='tw-overflow-hidden'
                          >
                            <TruncatedText label={item.label} />
                          </LinkComponent>
                        </BreadcrumbLink>
                      </DropdownMenuItem>
                    ))}
                  </DropdownMenuContent>
                </DropdownMenu>
                <BreadcrumbSeparator />
              </>
            ) : (
              <Fragment key={firstMiddleItem.label}>
                <BreadcrumbItem>
                  <BreadcrumbLink
                    dataTestId={firstMiddleItem.label
                      .toLowerCase()
                      .replace(/\s+/g, '-')}
                    asChild
                  >
                    <LinkComponent
                      href={firstMiddleItem.url}
                      className='tw-overflow-hidden'
                    >
                      <TruncatedText label={firstMiddleItem.label} />
                    </LinkComponent>
                  </BreadcrumbLink>
                </BreadcrumbItem>
                <BreadcrumbSeparator />
              </Fragment>
            )}
          </>
        )}
        {hasSecondToLastItem && showSecondToLastItem && (
          <>
            <BreadcrumbItem className='tw-hidden md:tw-flex'>
              <BreadcrumbLink
                dataTestId={secondToLast.label
                  .toLowerCase()
                  .replace(/\s+/g, '-')}
                asChild
              >
                <LinkComponent
                  href={secondToLast.url}
                  className='tw-overflow-hidden'
                >
                  <TruncatedText label={secondToLast.label} />
                </LinkComponent>
              </BreadcrumbLink>
            </BreadcrumbItem>
            <BreadcrumbSeparator />
          </>
        )}
        {hasLastItem && (
          <BreadcrumbItem>
            {last.url ? (
              <BreadcrumbLink
                dataTestId={last.label.toLowerCase().replace(/\s+/g, '-')}
                asChild
              >
                <LinkComponent href={last.url} className='tw-overflow-hidden'>
                  <TruncatedText label={last.label} />
                </LinkComponent>
              </BreadcrumbLink>
            ) : (
              <BreadcrumbPage className='tw-overflow-hidden'>
                <TruncatedText label={last.label} />
              </BreadcrumbPage>
            )}
          </BreadcrumbItem>
        )}
        {showSeparatorAfterLastItem && <BreadcrumbSeparator />}
      </BreadcrumbList>
    </Breadcrumb>
  )
}

interface TruncatedTextProps {
  dataTestId?: string
  label: string
}

const TruncatedText: FC<TruncatedTextProps> = ({ label, dataTestId }) => {
  const { isOverflowing, textRef } = useIsOverflowing(label)

  return (
    <Tooltip content={label} active={isOverflowing} withArrow>
      <span
        ref={textRef}
        data-testid={dataTestId}
        className='tw-block tw-truncate'
      >
        {label}
      </span>
    </Tooltip>
  )
}
